import type { FetchClient } from "@partners/ui/fetch-client";

import { useAuth0 } from "@auth0/auth0-react";
import { EnrichedFetchError, readResponse } from "@partners/ui/fetch-client";

import { getAPIBaseUrl, isServedToGSRDomains } from "@/utils/domainResolver";
import { logger } from "@/utils/logger";

const stripNullQueryParams = (path: string): string => {
  const queryIndex = path.indexOf("?");
  if (queryIndex === -1) return path;

  const pathname = path.slice(0, queryIndex);
  const queryParams = new URLSearchParams();

  for (const [key, value] of new URLSearchParams(path.slice(queryIndex + 1))) {
    if (value !== "null") {
      queryParams.append(key, value);
    }
  }

  const queryString = queryParams.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
};

export const usePortalFetchClient = (): FetchClient => {
  const auth = useAuth0();

  return async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    const { headers, ...restInit } = init;
    const {
      Authorization: upperCaseAuth,
      authorization: lowerCaseAuth,
      ...restHeaders
    } = (headers ?? {}) as Record<string, string>;
    const authorization =
      upperCaseAuth ??
      lowerCaseAuth ??
      `Bearer ${await auth.getAccessTokenSilently()}`;

    const baseURL = getAPIBaseUrl(import.meta.env.VITE_API_HOST);
    const fullUrl = `${baseURL}${stripNullQueryParams(path)}`;

    let response: Response;
    try {
      response = await fetch(fullUrl, {
        ...restInit,
        headers: {
          ...restHeaders,
          Authorization: authorization,
          "X-Client-Source": "portal",
          ...(isServedToGSRDomains()
            ? { "X-GSR-Domain": `https://${window.location.hostname}` }
            : {}),
        },
      });
    } catch (error) {
      if (!init.signal?.aborted) {
        const message = error instanceof Error ? error.message : String(error);
        logger.error(
          new Error(`Error when calling ${fullUrl}. Error: ${message}.`),
        );
      }
      throw error;
    }

    try {
      return await readResponse<T>(response, fullUrl);
    } catch (error) {
      if (error instanceof EnrichedFetchError) logger.error(error);
      throw error;
    }
  };
};
